import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowUp, ArrowDown, Trash2, Save, Plus } from "lucide-react";

export function FinderOptionsDialog({ group, title, description, withQuery = false, open, onOpenChange, onChanged }) {
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nl, setNl] = useState("");
  const [nq, setNq] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/finder-options?group=${group}`)
      .then(({ data }) => setOpts(data.options || []))
      .catch(() => setOpts([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open, group]);

  const changed = () => { load(); onChanged && onChanged(); };
  const setF = (id, k, v) => setOpts((a) => a.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  const add = async () => {
    const label = nl.trim();
    if (!label) return toast.error("Inserisci un nome");
    setBusy(true);
    try {
      await api.post("/admin/finder-options", { group, label, query: withQuery ? nq.trim() : "" });
      setNl(""); setNq(""); changed(); toast.success("Voce aggiunta");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const save = async (o) => {
    try {
      await api.patch(`/admin/finder-options/${o.id}`, withQuery ? { label: o.label, query: o.query || "" } : { label: o.label });
      changed(); toast.success("Salvato");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async (o) => {
    if (!window.confirm(`Eliminare "${o.label}"?`)) return;
    try { await api.delete(`/admin/finder-options/${o.id}`); changed(); toast.success("Voce eliminata"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= opts.length) return;
    const arr = [...opts];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setOpts(arr);
    try { await api.post("/admin/finder-options/reorder", { ids: arr.map((x) => x.id) }); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); load(); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="finder-options-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description || "Crea, modifica, riordina o elimina le opzioni del menu a tendina di ricerca."}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
        ) : (
          <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
            {opts.map((o, idx) => (
              <div key={o.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background/40 p-2" data-testid={`finder-opt-row-${o.id}`}>
                <div className="flex flex-col">
                  <Button variant="ghost" size="icon" className="h-5 w-6" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`finder-opt-up-${o.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-5 w-6" disabled={idx === opts.length - 1} onClick={() => move(idx, 1)} data-testid={`finder-opt-down-${o.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                </div>
                <Input value={o.label} onChange={(e) => setF(o.id, "label", e.target.value)} className="h-9 min-w-[160px] flex-1" placeholder="Nome" data-testid={`finder-opt-label-${o.id}`} />
                {withQuery && <Input value={o.query || ""} onChange={(e) => setF(o.id, "query", e.target.value)} className="h-9 min-w-[200px] flex-1" placeholder="Suggerimento ricerca AI (opzionale)" data-testid={`finder-opt-query-${o.id}`} />}
                <Button variant="outline" size="sm" onClick={() => save(o)} data-testid={`finder-opt-save-${o.id}`}><Save className="mr-1.5 h-3.5 w-3.5" /> Salva</Button>
                <Button variant="ghost" size="sm" onClick={() => del(o)} data-testid={`finder-opt-delete-${o.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            ))}
            {!opts.length && <div className="py-4 text-sm text-muted-foreground">Nessuna voce. Aggiungine una qui sotto.</div>}
          </div>
        )}

        <div className="mt-2 space-y-2 border-t border-border pt-3">
          <Label className="text-xs">Nuova voce</Label>
          <div className="flex flex-wrap items-end gap-2">
            <Input value={nl} onChange={(e) => setNl(e.target.value)} placeholder="Nome della voce" className="h-9 min-w-[160px] flex-1" data-testid="finder-opt-new-label" />
            {withQuery && <Input value={nq} onChange={(e) => setNq(e.target.value)} placeholder="Suggerimento ricerca AI (opzionale)" className="h-9 min-w-[200px] flex-1" data-testid="finder-opt-new-query" />}
            <Button onClick={add} disabled={busy} data-testid="finder-opt-add"><Plus className="mr-1.5 h-4 w-4" /> Aggiungi</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
